import { useMutation, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../api';
import { SetupDiagram } from '../components/SetupDiagram';
import type { Race, RaceSettings, Station, TimingPoint } from '../types';
import { liveKey, raceKey, useLive, useRace } from './queries';

function PairingModal({ code, station, onClose }: { code: string; station: Station; onClose: () => void }) {
  const url = `${location.origin}/station?code=${encodeURIComponent(code)}`;
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    QRCode.toDataURL(url, { margin: 1, width: 240 }).then(setQr, () => setQr(null));
  }, [url]);
  return (
    <div className="modal-back" role="dialog" aria-modal="true" aria-labelledby="pair-title" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 id="pair-title" className="display" style={{ fontSize: 24 }}>
          Pair “{station.label}”
        </h2>
        <p className="muted" style={{ margin: 0 }}>
          The volunteer scans this with their phone camera, or opens <b>{location.host}/station</b> and types the code. No app install needed.
        </p>
        <div className="inline" style={{ gap: 20 }}>
          {qr ? <img src={qr} width={160} height={160} alt={`QR code for pairing code ${code}`} style={{ borderRadius: 8, background: '#fff' }} /> : null}
          <div className="mono" style={{ fontSize: 30, letterSpacing: '0.08em' }}>
            {code}
          </div>
        </div>
        <p className="small muted" style={{ margin: 0 }}>
          This code is shown once and works for 7 days. You can issue a new one from the station’s row.
        </p>
        <button type="button" className="btn primary" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}

function AddStationForm({ raceId, point, onCreated }: { raceId: string; point: TimingPoint; onCreated: (s: Station, code: string) => void }) {
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const [role, setRole] = useState<Station['role']>(point.kind === 'finish' ? 'timing' : 'both');
  const [volunteer, setVolunteer] = useState('');
  const create = useMutation({
    mutationFn: () =>
      api<{ station: Station; pairingCode: string }>(`/races/${raceId}/stations`, {
        body: { timingPointId: point.id, label: label || point.name, role, volunteerName: volunteer || null },
      }),
    onSuccess: ({ station, pairingCode }) => {
      setLabel('');
      setVolunteer('');
      qc.invalidateQueries({ queryKey: raceKey(raceId) });
      qc.invalidateQueries({ queryKey: liveKey(raceId) });
      onCreated(station, pairingCode);
    },
  });
  return (
    <form
      className="form-grid"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <label className="field">
        Station name
        <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={`${point.name} A`} />
      </label>
      <label className="field">
        Camera job
        <select className="input" value={role} onChange={(e) => setRole(e.target.value as Station['role'])}>
          <option value="both">Times and bibs: in line with the finish, turned towards runners</option>
          <option value="timing">Timing: in line with the finish, across the course</option>
          <option value="bib">Bibs only: facing runners before the line</option>
        </select>
      </label>
      <label className="field">
        Volunteer
        <input className="input" value={volunteer} onChange={(e) => setVolunteer(e.target.value)} placeholder="Optional" />
      </label>
      <button type="submit" className="btn" disabled={create.isPending}>
        Add station + pairing code
      </button>
      {create.error && <div className="notice bad">{(create.error as Error).message}</div>}
    </form>
  );
}

function StationRow({ station, onNewCode }: { station: Station; onNewCode: (code: string) => void }) {
  const qc = useQueryClient();
  const reissue = useMutation({
    mutationFn: () => api<{ pairingCode: string }>(`/stations/${station.id}/pairing-code`, { body: {} }),
    onSuccess: ({ pairingCode }) => onNewCode(pairingCode),
  });
  const remove = useMutation({
    mutationFn: () => api(`/stations/${station.id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: raceKey(station.raceId) }),
  });
  const roleLabel = { timing: 'Timing camera', bib: 'Bib camera', both: 'Timing + bibs' }[station.role];
  return (
    <div className="inline" style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 12, justifyContent: 'space-between' }}>
      <div style={{ minWidth: 220 }}>
        <div style={{ fontWeight: 600 }}>{station.label}</div>
        <div className="small muted">
          {roleLabel}
          {station.volunteerName ? ` · ${station.volunteerName}` : ''}
          {station.deviceLabel ? ` · ${station.deviceLabel}` : ''}
        </div>
      </div>
      <div className="inline">
        <span className={`chip ${station.paired ? 'ok' : 'neutral'}`}>{station.paired ? 'Paired' : 'Not paired'}</span>
        <span className={`chip ${station.line ? 'ok' : 'warn'}`}>{station.line ? 'Line set' : 'Line not set'}</span>
        <button type="button" className="btn small" onClick={() => reissue.mutate()} disabled={reissue.isPending}>
          New pairing code
        </button>
        <button
          type="button"
          className="btn small"
          onClick={() => {
            if (confirm(`Remove ${station.label}? Its phone will stop working.`)) remove.mutate();
          }}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

/**
 * What to add at a timing point, from the placement study: timing needs a
 * camera in line with the finish; bibs are read far better by a second phone
 * facing runners; busy finishes hide runners from a single side-on camera.
 */
function setupAdvice(point: TimingPoint, stations: Station[], runners: number): { tone: 'ok' | 'warn' | 'bad'; text: string }[] {
  const here = stations.filter((s) => s.timingPointId === point.id);
  const timing = here.filter((s) => s.role !== 'bib');
  const bib = here.filter((s) => s.role !== 'timing');
  const out: { tone: 'ok' | 'warn' | 'bad'; text: string }[] = [];
  if (here.length === 0) return [{ tone: 'bad', text: 'No camera yet. Add one below.' }];
  if (timing.length === 0) out.push({ tone: 'bad', text: 'No timing camera: a bib camera alone can say who passed, not exactly when. Add a phone in line with the line.' });
  if (here.length === 1 && here[0]!.role === 'both') out.push({ tone: 'warn', text: 'One phone does both jobs. A second phone facing runners reads many more bibs, so fewer crossings need review.' });
  if (point.kind === 'finish' && timing.length === 1 && runners >= 300) {
    out.push({ tone: 'warn', text: `With ${runners} runners the finish gets busy: add a second timing phone on the other side of the line (or overhead on the arch). One side-on camera can’t see a runner hidden behind another.` });
  }
  if (bib.length > 0 && timing.length > 0 && out.length === 0) out.push({ tone: 'ok', text: 'Good setup: timing in line with the finish, bibs read from the front.' });
  return out;
}

function SettingsPanel({ race }: { race: Race }) {
  const qc = useQueryClient();
  const [s, setS] = useState<RaceSettings>(race.settings);
  const [status, setStatus] = useState(race.status);
  const save = useMutation({
    mutationFn: () => api<{ race: Race }>(`/races/${race.id}`, { method: 'PATCH', body: { settings: s, status } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: raceKey(race.id) });
      qc.invalidateQueries({ queryKey: liveKey(race.id) });
    },
  });
  const resetGun = useMutation({
    mutationFn: () => api(`/races/${race.id}`, { method: 'PATCH', body: { gunTimeMs: null } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: raceKey(race.id) }),
  });
  return (
    <section className="card stack" style={{ gap: 16 }}>
      <h2>Capture &amp; detection</h2>
      <label className="field">
        Video chunk length
        <select className="input" value={s.chunkSeconds} onChange={(e) => setS({ ...s, chunkSeconds: Number(e.target.value) as RaceSettings['chunkSeconds'] })}>
          <option value={2}>2 s · fastest results, more uploads</option>
          <option value={4}>4 s · balanced</option>
          <option value={8}>8 s · fewest uploads, weak signal</option>
        </select>
      </label>
      <label className="field">
        Frame rate
        <select className="input" value={s.fps} onChange={(e) => setS({ ...s, fps: Number(e.target.value) as RaceSettings['fps'] })}>
          <option value={30}>30 fps · works on every phone</option>
          <option value={60}>60 fps · sharper bibs and finer times, larger uploads</option>
        </select>
      </label>
      <label className="field">
        Video quality
        <select className="input" value={s.videoMbps} onChange={(e) => setS({ ...s, videoMbps: Number(e.target.value) })}>
          <option value={6}>6 Mbps · weak signal (bibs read less often)</option>
          <option value={12}>12 Mbps · recommended</option>
          <option value={16}>16 Mbps · best bibs, about 8 MB per 4 s chunk</option>
        </select>
        <span className="hint">Bib digits blur away at low bitrates. Upload use per phone ≈ this number, while runners are passing.</span>
      </label>
      <label className="field">
        <span className="inline" style={{ justifyContent: 'space-between' }}>
          <span>Send to review below this confidence</span>
          <span className="mono">{s.confidenceThreshold.toFixed(2)}</span>
        </span>
        <input type="range" min={0.5} max={0.99} step={0.01} value={s.confidenceThreshold} onChange={(e) => setS({ ...s, confidenceThreshold: Number(e.target.value) })} style={{ accentColor: 'var(--accent)' }} />
        <span className="hint">Higher means more crossings go to a human or the larger model: more accurate, more work.</span>
      </label>
      <label className="check">
        <input type="checkbox" checked={s.netTimes} onChange={(e) => setS({ ...s, netTimes: e.target.checked })} />
        <span>
          <b>Net times from the start station</b>
          <br />
          <span className="muted">Each runner’s clock starts when they cross the start line, not at the gun.</span>
        </span>
      </label>
      <label className="check">
        <input type="checkbox" checked={s.lookalikeAutoAssign ?? true} onChange={(e) => setS({ ...s, lookalikeAutoAssign: e.target.checked })} />
        <span>
          <b>Recognise runners by appearance</b>
          <br />
          <span className="muted">
            When a bib is hidden, Claude compares the runner with photos from points where their bib was read. It assigns the time only when it’s sure, their pace fits and no lookalike does; otherwise it suggests. Turn off to always decide yourself.
          </span>
        </span>
      </label>
      <label className="field">
        Keep raw video for
        <select className="input" value={s.retainRawDays} onChange={(e) => setS({ ...s, retainRawDays: Number(e.target.value) as RaceSettings['retainRawDays'] })}>
          <option value={7}>7 days</option>
          <option value={30}>30 days</option>
          <option value={90}>90 days</option>
        </select>
      </label>
      <label className="field">
        Race status
        <select className="input" value={status} onChange={(e) => setStatus(e.target.value as Race['status'])}>
          <option value="draft">Draft (setting up)</option>
          <option value="live">Live</option>
          <option value="closed">Closed (stops uploads)</option>
        </select>
      </label>
      {save.error && <div className="notice bad">{(save.error as Error).message}</div>}
      <div className="inline">
        <button type="button" className="btn primary" onClick={() => save.mutate()} disabled={save.isPending}>
          Save settings
        </button>
        {race.gunTimeMs && (
          <button
            type="button"
            className="btn"
            onClick={() => {
              if (confirm('Clear the gun time? Use this only for a false start or a test run.')) resetGun.mutate();
            }}
          >
            Clear gun time
          </button>
        )}
      </div>
    </section>
  );
}

export function CoursePage() {
  const { raceId = '' } = useParams();
  const qc = useQueryClient();
  const race = useRace(raceId);
  const live = useLive(raceId);
  const [pairing, setPairing] = useState<{ station: Station; code: string } | null>(null);
  const [name, setName] = useState('');
  const [km, setKm] = useState('');
  const [kind, setKind] = useState<TimingPoint['kind']>('split');

  const addPoint = useMutation({
    mutationFn: () => api(`/races/${raceId}/timing-points`, { body: { name, km: Number(km), kind } }),
    onSuccess: () => {
      setName('');
      setKm('');
      qc.invalidateQueries({ queryKey: raceKey(raceId) });
      qc.invalidateQueries({ queryKey: liveKey(raceId) });
    },
  });
  const removePoint = useMutation({
    mutationFn: (id: string) => api(`/timing-points/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: raceKey(raceId) }),
  });

  if (!race.data) return <main className="page">{race.error ? <div className="notice bad">{(race.error as Error).message}</div> : 'Loading…'}</main>;
  const { timingPoints, stations } = race.data;
  const maxKm = Math.max(1, ...timingPoints.map((p) => p.km));

  const submitPoint = (e: FormEvent) => {
    e.preventDefault();
    addPoint.mutate();
  };

  return (
    <main className="page">
      <section className="page-head">
        <div>
          <h1>Course &amp; stations</h1>
          <p>Add a timing point wherever you want a split, then pair one or more phones to it. At the finish, a side-on timing camera plus a head-on bib camera catches far more bibs than one phone.</p>
        </div>
      </section>

      <details className="card" style={{ padding: '14px 18px' }} open={stations.length === 0}>
        <summary style={{ fontWeight: 600, cursor: 'pointer' }}>Where do the phones go?</summary>
        <div className="stack" style={{ gap: 10, marginTop: 10 }}>
          <p style={{ margin: 0 }}>
            At each timing point, a <b>timing phone</b> stands exactly in line with the line, off the side of the course, and decides the times. A <b>bib phone</b> faces runners a few metres before the line and reads bibs. A cone at the far end of the line lets the volunteer check the timing phone is lined up.
          </p>
          <SetupDiagram />
          <div className="inline small">
            <Link to="/docs/camera-placement">Camera placement guide</Link>
            <Link to="/docs/planning">How many phones?</Link>
            <Link to="/docs/start-line">The start line</Link>
            <Link to="/docs/equipment">Equipment</Link>
          </div>
        </div>
      </details>

      {timingPoints.length > 0 && (
        <section aria-label="Course timeline" className="card" style={{ overflowX: 'auto' }}>
          <div style={{ position: 'relative', minWidth: 640, height: 96, margin: '0 40px' }}>
            <div style={{ position: 'absolute', left: 0, right: 0, top: 40, height: 6, borderRadius: 3, background: 'var(--ink)' }} />
            {timingPoints.map((p) => (
              <div key={p.id} style={{ position: 'absolute', left: `${(p.km / maxKm) * 100}%`, top: 0, transform: 'translateX(-50%)', textAlign: 'center' }}>
                <div className="display" style={{ fontSize: 16, whiteSpace: 'nowrap' }}>
                  {p.name}
                </div>
                <div style={{ width: 20, height: 20, borderRadius: 999, background: 'var(--accent)', border: '3px solid var(--ink)', margin: '4px auto 0' }} />
                <div className="small muted mono" style={{ marginTop: 6 }}>
                  {p.km} km · {stations.filter((s) => s.timingPointId === p.id).length} phone{stations.filter((s) => s.timingPointId === p.id).length === 1 ? '' : 's'}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="row">
        <section className="main-col">
          <h2 className="section-title">Timing points</h2>
          {timingPoints.map((p) => (
            <div className="card stack" key={p.id} style={{ gap: 14 }}>
              <div className="inline" style={{ alignItems: 'baseline' }}>
                <div className="display" style={{ fontSize: 22 }}>
                  {p.name}
                </div>
                <div className="mono small muted">{p.km} km</div>
                <div className="small muted" style={{ marginRight: 'auto' }}>
                  {p.kind === 'start' ? 'Sets each runner’s net start' : p.kind === 'finish' ? 'Official finish' : 'Split'}
                </div>
                <button
                  type="button"
                  className="btn small"
                  onClick={() => {
                    if (confirm(`Delete ${p.name} and its stations?`)) removePoint.mutate(p.id);
                  }}
                >
                  Delete
                </button>
              </div>
              {setupAdvice(p, stations, live.data?.counts.runners ?? 0).map((a) => (
                <div key={a.text} className={`notice ${a.tone}`}>
                  {a.text}
                </div>
              ))}
              {stations
                .filter((s) => s.timingPointId === p.id)
                .map((s) => (
                  <StationRow key={s.id} station={s} onNewCode={(code) => setPairing({ station: s, code })} />
                ))}
              <AddStationForm raceId={raceId} point={p} onCreated={(station, code) => setPairing({ station, code })} />
            </div>
          ))}

          <form className="card stack" onSubmit={submitPoint}>
            <h2>Add a timing point</h2>
            <div className="form-grid">
              <label className="field">
                Name
                <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="5K split" required />
              </label>
              <label className="field">
                Distance from start (km)
                <input className="input" type="number" inputMode="decimal" min={0} step="0.001" value={km} onChange={(e) => setKm(e.target.value)} required />
              </label>
              <label className="field">
                Kind
                <select className="input" value={kind} onChange={(e) => setKind(e.target.value as TimingPoint['kind'])}>
                  <option value="start">Start</option>
                  <option value="split">Split</option>
                  <option value="finish">Finish</option>
                </select>
              </label>
              <button type="submit" className="btn primary" disabled={addPoint.isPending}>
                Add timing point
              </button>
            </div>
            {addPoint.error && <div className="notice bad">{(addPoint.error as Error).message}</div>}
          </form>
        </section>

        <aside className="side-col">
          <SettingsPanel race={race.data.race} key={race.data.race.id + race.data.race.status} />
        </aside>
      </div>

      {pairing && <PairingModal code={pairing.code} station={pairing.station} onClose={() => setPairing(null)} />}
    </main>
  );
}
